import { NavLink } from 'react-router-dom';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import GppMaybeRoundedIcon from '@mui/icons-material/GppMaybeRounded';
import TroubleshootRoundedIcon from '@mui/icons-material/TroubleshootRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import MailLockRoundedIcon from '@mui/icons-material/MailLockRounded';
import TerminalRoundedIcon from '@mui/icons-material/TerminalRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { tokens } from '../../theme/theme';
import BrandLogo from '../branding/BrandLogo';

const DRAWER_WIDTH = 236;

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', path: '/', icon: SpaceDashboardRoundedIcon }]
  },
  {
    label: 'Security Operations',
    items: [
      { label: 'Threat Detection', path: '/threats', icon: GppMaybeRoundedIcon },
      { label: 'Threat Monitoring', path: '/threat-monitoring', icon: RadarRoundedIcon },
      { label: 'Threat Intelligence', path: '/threat-intel', icon: PublicRoundedIcon },
      { label: 'Vulnerabilities', path: '/vulnerabilities', icon: TroubleshootRoundedIcon },
      { label: 'Vulnerability Management', path: '/vulnerability-management', icon: BugReportRoundedIcon },
      { label: 'Incident Management', path: '/incidents', icon: ListAltRoundedIcon },
      { label: 'SOC', path: '/soc', icon: HeadsetMicRoundedIcon },
      { label: 'Endpoint Protection', path: '/endpoint', icon: DevicesRoundedIcon },
      { label: 'Email Security', path: '/email', icon: MailLockRoundedIcon },
      { label: 'Network Discovery', path: '/network-discovery', icon: LanRoundedIcon },
      { label: 'Malware Analysis', path: '/malware-analysis', icon: BugReportRoundedIcon },
      { label: 'Pentest Toolkit', path: '/pentest', icon: TerminalRoundedIcon },
      { label: 'Simulation Lab', path: '/simlab', icon: ScienceRoundedIcon },
      { label: 'AI Intelligence', path: '/ai-intelligence', icon: AutoAwesomeRoundedIcon },
      { label: 'Automation & Response', path: '/automation', icon: BoltRoundedIcon }
    ]
  },
  {
    label: 'Platform Management',
    items: [
      { label: 'Identity & Access', path: '/identity', icon: BadgeRoundedIcon },
      { label: 'Tickets', path: '/tickets', icon: ConfirmationNumberRoundedIcon },
      { label: 'Notifications', path: '/notifications', icon: NotificationsActiveRoundedIcon },
      { label: 'Audit Logs', path: '/audit-logs', icon: FactCheckRoundedIcon },
      { label: 'Settings & Security', path: '/settings', icon: TuneRoundedIcon },
      { label: 'Organizations', path: '/organizations', icon: BusinessRoundedIcon },
      { label: 'Asset Management', path: '/assets', icon: InventoryRoundedIcon },
      { label: 'Reports & Analytics', path: '/reports', icon: AssessmentRoundedIcon },
      { label: 'Compliance', path: '/compliance', icon: VerifiedUserRoundedIcon },
      { label: 'Security Policies', path: '/policies', icon: GavelRoundedIcon },
      { label: 'Scheduler', path: '/scheduler', icon: ScheduleRoundedIcon },
      { label: 'Subscription & Licensing', path: '/licensing', icon: WorkspacePremiumRoundedIcon }
    ]
  }
];

export default function SidebarContent({ onNavigate }) {
  return (
    <Box sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: tokens.bg.surface }}>
      <Box sx={{ px: 2.2, py: 2.1 }}>
        <BrandLogo compact dark />
        <Typography variant="overline" sx={{ color: tokens.brand.main, display: 'block', mt: 1.1, fontSize: '0.58rem' }}>CyberGuard Security Console</Typography>
      </Box>
      <Divider sx={{ borderColor: tokens.border }} />
      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {NAV_GROUPS.map((group) => (
          <List
            key={group.label}
            subheader={
              <Typography sx={{ px: 2.2, pt: 1.4, pb: 0.4, fontSize: '0.65rem', color: tokens.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {group.label}
              </Typography>
            }
            sx={{ px: 1.2, py: 0 }}
          >
            {group.items.map(({ label, path, icon: Icon }) => (
              <ListItemButton
                key={path}
                component={NavLink}
                to={path}
                onClick={onNavigate}
                end={path === '/'}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.4,
                  color: tokens.text.secondary,
                  '&.active': {
                    backgroundColor: tokens.brand.glow,
                    color: tokens.text.primary,
                    '& .MuiListItemIcon-root': { color: tokens.brand.main }
                  },
                  '&:hover': { backgroundColor: tokens.bg.elevated }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  {label}
                </ListItemText>
              </ListItemButton>
            ))}
          </List>
        ))}
      </Box>
      <Box sx={{ p: 2, borderTop: `1px solid ${tokens.border}` }}>
        <Typography variant="caption" sx={{ color: tokens.text.muted, fontFamily: '"JetBrains Mono", monospace' }}>
          v1.0.0 · env: production
        </Typography>
      </Box>
    </Box>
  );
}

export { DRAWER_WIDTH };
